/**
 * Enterprise-Level Error Reporting Service
 *
 * Sends client-side errors to the backend for database logging and email alerts.
 * This service captures React errors, unhandled promise rejections,
 * and general JavaScript errors with comprehensive context.
 */
import { getConfig } from '../config/Constants'

// ============================================================================
// Types
// ============================================================================

interface ErrorReport {
    error_type: string
    error_message: string
    stack_trace?: string
    component_stack?: string
    url: string
    user_agent?: string
    user_id?: string
    user_email?: string
    additional_context?: Record<string, any>
    // Enhanced fields
    screen_resolution?: string
    timestamp?: string
    session_id?: string
    page_title?: string
}

interface ErrorReportResponse {
    success: boolean
    message: string
    error_id: string
}

// ============================================================================
// Safe localStorage Access (iOS compatible)
// ============================================================================

function safeLocalStorageGet(key: string): string | null {
    try {
        return localStorage.getItem(key)
    } catch (error) {
        console.warn(`[ErrorReporting] localStorage.getItem('${key}') failed:`, error)
        return null
    }
}

// ============================================================================
// Session ID Management
// ============================================================================

let sessionId: string | null = null

function getSessionId(): string {
    if (!sessionId) {
        // Try to get from sessionStorage first
        try {
            sessionId = sessionStorage.getItem('error_session_id')
            if (!sessionId) {
                sessionId = `sess_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
                sessionStorage.setItem('error_session_id', sessionId)
            }
        } catch (e) {
            // Fallback if sessionStorage is not available
            sessionId = `sess_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
        }
    }
    return sessionId
}

// ============================================================================
// User Info Retrieval
// ============================================================================

function getCurrentUserInfo(): { user_id?: string; user_email?: string } {
    try {
        const userStr = safeLocalStorageGet('user')
        if (userStr && userStr !== 'undefined' && userStr !== 'null') {
            const user = JSON.parse(userStr)
            return {
                user_id: user?.id,
                user_email: user?.email
            }
        }
    } catch (e) {
        // Ignore errors reading user data
    }
    return {}
}

// ============================================================================
// Error Message Extraction
// ============================================================================

/**
 * Extract a meaningful error message from any value.
 * Handles Error objects, strings, objects, and edge cases.
 */
function extractErrorMessage(value: any): string {
    // Handle null/undefined
    if (value === null) return 'Error: null value'
    if (value === undefined) return 'Error: undefined value'

    // Handle Error instances
    if (value instanceof Error) {
        return value.message || value.name || 'Unknown Error'
    }

    // Handle strings
    if (typeof value === 'string') {
        return value || 'Empty string error'
    }

    // Handle objects with message property
    if (typeof value === 'object') {
        if (value.message) return String(value.message)
        if (value.error) return String(value.error)
        if (value.reason) return String(value.reason)
        if (value.detail) return String(value.detail)

        // Try to stringify the object
        try {
            const str = JSON.stringify(value)
            if (str && str !== '{}') {
                return `Object: ${str.substring(0, 500)}`
            }
        } catch (e) {
            // Can't stringify, use toString
        }

        return value.toString?.() || 'Unknown object error'
    }

    // Handle other types
    return String(value) || 'Unknown error'
}

/**
 * Extract stack trace from any value
 */
function extractStackTrace(value: any): string | undefined {
    // If it's an Error, use its stack
    if (value instanceof Error && value.stack) {
        return value.stack
    }

    // If it's an object with a stack property
    if (typeof value === 'object' && value?.stack) {
        return String(value.stack)
    }

    // Generate a stack trace at the current location
    try {
        const syntheticError = new Error('Synthetic stack trace')
        return syntheticError.stack
    } catch (e) {
        return undefined
    }
}

/**
 * Extract error type/name from any value
 */
function extractErrorType(value: any, defaultType: string = 'UnknownError'): string {
    if (value instanceof Error) {
        return value.name || value.constructor?.name || defaultType
    }

    if (typeof value === 'object' && value !== null) {
        if (value.name) return String(value.name)
        if (value.code) return `ErrorCode_${value.code}`
        if (value.type) return String(value.type)
    }

    return defaultType
}

// ============================================================================
// Device/Browser Info
// ============================================================================

function getScreenResolution(): string {
    try {
        return `${window.screen.width}x${window.screen.height}`
    } catch (e) {
        return 'unknown'
    }
}

// ============================================================================
// Main Error Reporting Function
// ============================================================================

// ============================================================================
// Noise control: don't flood the server with repeats of the same problem
// ============================================================================

const DEDUPE_WINDOW_MS = 60_000
const MAX_REPORTS_PER_PAGE_LOAD = 15
const recentReports = new Map<string, number>()
let reportsThisPageLoad = 0

function shouldSendReport(errorData: Partial<ErrorReport>): boolean {
    try {
        if (reportsThisPageLoad >= MAX_REPORTS_PER_PAGE_LOAD) return false
        const resourceUrl = String(errorData.additional_context?.['Resource URL'] ?? '')
        const signature = [
            errorData.error_type || '',
            (errorData.error_message || '').substring(0, 200),
            resourceUrl,
        ].join('|')
        const now = Date.now()
        const last = recentReports.get(signature)
        if (last !== undefined && now - last < DEDUPE_WINDOW_MS) return false
        recentReports.set(signature, now)
        if (recentReports.size > 200) {
            // keep memory bounded
            const oldest = recentReports.keys().next().value
            if (oldest !== undefined) recentReports.delete(oldest)
        }
        reportsThisPageLoad += 1
        return true
    } catch (e) {
        return true
    }
}

/** Only report images that come from our own site or our own file storage. */
function isOwnResource(src: string): boolean {
    try {
        const host = new URL(src, window.location.href).hostname
        return host === window.location.hostname ||
            host.endsWith('ai-mastery-hub.com') ||
            host.endsWith('roberthogan.io') ||
            host.includes('aiacademy-course-videos')
    } catch (e) {
        return true
    }
}

/**
 * Report an error to the backend with full context
 */
async function reportError(errorData: Partial<ErrorReport>): Promise<ErrorReportResponse | null> {
    if (!shouldSendReport(errorData)) return null
    try {
        const config = getConfig()
        const userInfo = getCurrentUserInfo()

        const report: ErrorReport = {
            error_type: errorData.error_type || 'UnknownError',
            error_message: errorData.error_message || 'Unknown error occurred',
            stack_trace: errorData.stack_trace,
            component_stack: errorData.component_stack,
            url: errorData.url || window.location.href,
            user_agent: navigator.userAgent,
            user_id: userInfo.user_id,
            user_email: userInfo.user_email,
            additional_context: errorData.additional_context,
            // Enhanced fields
            screen_resolution: getScreenResolution(),
            timestamp: new Date().toISOString(),
            session_id: getSessionId(),
            page_title: document.title
        }

        // Use fetch with keepalive for reliability
        const response = await fetch(`${config.apiUrl}/errors/report`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(report),
            keepalive: true
        })

        if (response.ok) {
            const data = await response.json()
            console.log(`[ErrorReporting] Error reported successfully. ID: ${data.error_id}`)
            return data
        } else {
            console.error('[ErrorReporting] Server returned error:', response.status)
            return null
        }
    } catch (e) {
        // Silently fail - we don't want error reporting to cause more errors
        console.error('[ErrorReporting] Failed to report error:', e)
        return null
    }
}

// ============================================================================
// Specialized Error Reporters
// ============================================================================

/**
 * Report a React Error Boundary catch
 */
function reportReactError(error: Error, componentStack: string): void {
    reportError({
        error_type: error.name || 'ReactError',
        error_message: error.message,
        stack_trace: error.stack,
        component_stack: componentStack,
        additional_context: {
            'Error Source': 'React Error Boundary',
            'Page': document.title,
            'Component Stack Available': !!componentStack
        }
    })
}

/**
 * Report an unhandled promise rejection with comprehensive error extraction
 */
function reportUnhandledRejection(reason: any): void {
    // Extract all possible information from the rejection reason
    const errorMessage = extractErrorMessage(reason)
    const stackTrace = extractStackTrace(reason)
    const errorType = extractErrorType(reason, 'UnhandledPromiseRejection')

    // Build additional context with as much info as possible
    const additionalContext: Record<string, any> = {
        'Error Source': 'Unhandled Promise Rejection',
        'Page': document.title,
        'Rejection Reason Type': typeof reason,
        'Is Error Instance': reason instanceof Error
    }

    // Add extra context for non-Error rejections
    if (!(reason instanceof Error)) {
        additionalContext['Raw Reason'] = (() => {
            try {
                if (reason === null) return 'null'
                if (reason === undefined) return 'undefined'
                if (typeof reason === 'object') {
                    return JSON.stringify(reason, null, 2).substring(0, 1000)
                }
                return String(reason).substring(0, 1000)
            } catch (e) {
                return 'Could not serialize reason'
            }
        })()
    }

    // If it's a DOMException (common on iOS), capture extra details
    if (reason instanceof DOMException) {
        additionalContext['DOMException Code'] = reason.code
        additionalContext['DOMException Name'] = reason.name
    }

    reportError({
        error_type: errorType,
        error_message: errorMessage,
        stack_trace: stackTrace,
        additional_context: additionalContext
    })
}

/**
 * Report a global JavaScript error
 */
function reportGlobalError(
    message: string,
    source?: string,
    lineno?: number,
    colno?: number,
    error?: Error
): void {
    reportError({
        error_type: error?.name || 'JavaScriptError',
        error_message: message,
        stack_trace: error?.stack,
        additional_context: {
            'Error Source': 'Global Error Handler',
            'Source File': source,
            'Line Number': lineno,
            'Column Number': colno,
            'Page': document.title,
            'Has Error Object': !!error
        }
    })
}

/**
 * Report a network/API error
 */
function reportNetworkError(
    url: string,
    method: string,
    status: number | string,
    errorMessage: string,
    responseBody?: string
): void {
    reportError({
        error_type: 'NetworkError',
        error_message: errorMessage,
        additional_context: {
            'Error Source': 'Network Request',
            'Request URL': url,
            'Request Method': method,
            'HTTP Status': status,
            'Page': document.title,
            'Response Preview': responseBody?.substring(0, 500)
        }
    })
}

/**
 * Report a custom application error with context
 */
function reportCustomError(
    errorType: string,
    errorMessage: string,
    context?: Record<string, any>,
    error?: Error
): void {
    reportError({
        error_type: errorType,
        error_message: errorMessage,
        stack_trace: error?.stack,
        additional_context: {
            'Error Source': 'Application',
            'Page': document.title,
            ...context
        }
    })
}

// ============================================================================
// Global Error Handler Initialization
// ============================================================================

/**
 * Initialize global error handlers
 * Call this once in your app's entry point (main.tsx)
 */
function initializeErrorHandlers(): void {
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
        console.error('[ErrorReporting] Unhandled Promise Rejection:', event.reason)
        reportUnhandledRejection(event.reason)
    })

    // Handle global JavaScript errors
    window.onerror = (message, source, lineno, colno, error) => {
        console.error('[ErrorReporting] Global Error:', message)
        reportGlobalError(
            typeof message === 'string' ? message : 'Unknown error',
            source,
            lineno,
            colno,
            error
        )
        // Return false to allow default error handling to continue
        return false
    }

    // Handle resource loading errors (images, scripts, etc.)
    window.addEventListener('error', (event) => {
        // Only handle resource errors, not script errors (those are handled by onerror)
        if (event.target && (event.target as HTMLElement).tagName) {
            const element = event.target as HTMLElement
            const tagName = element.tagName.toLowerCase()

            // Only report script/link/img loading failures
            if (['script', 'link', 'img'].includes(tagName)) {
                const src = (element as HTMLImageElement | HTMLScriptElement).src ||
                           (element as HTMLLinkElement).href || 'unknown'

                // Third-party images failing (slow connections, extensions) are not our problem
                if (tagName === 'img' && !isOwnResource(src)) return

                reportError({
                    error_type: 'ResourceLoadError',
                    error_message: `Failed to load ${tagName}: ${src}`,
                    additional_context: {
                        'Error Source': 'Resource Loading',
                        'Resource Type': tagName,
                        'Resource URL': src,
                        'Page': document.title
                    }
                })
            }
        }
    }, true) // Use capture phase to catch resource errors

    console.log('[ErrorReporting] Global error handlers initialized')
}

// ============================================================================
// Export Service
// ============================================================================

export const errorReportingService = {
    reportError,
    reportReactError,
    reportUnhandledRejection,
    reportGlobalError,
    reportNetworkError,
    reportCustomError,
    initializeErrorHandlers
}
